import {
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core'

import type { TripStop } from 'tripPlanner/api/types'
import {
  DROP_ANIMATION_DURATION,
  INTERACTIVE_DRAG_IGNORE_SELECTORS,
  POINTER_DRAG_ACTIVATION_DISTANCE,
  TOUCH_DRAG_ACTIVATION_DELAY,
  TOUCH_DRAG_ACTIVATION_TOLERANCE,
  createDragEventSensors,
  getDropPointFromDragEvent,
  getPointerCoordinates,
  moveItemToDropTarget,
  moveItemToGroupTarget,
  type DropTargetPlacement,
  type PointerCoordinates,
} from '../tripDragReorder'
import type { TripListMoveDayOption, TripListMovePlacement } from './tripListMoveDayOptions'
import type { TripListGroup, TripListRow } from './tripListTypes'
import { getTripListRowTripItemId } from './tripListCartPrices'

export {
  DROP_ANIMATION_DURATION,
  POINTER_DRAG_ACTIVATION_DISTANCE,
  TOUCH_DRAG_ACTIVATION_DELAY,
  TOUCH_DRAG_ACTIVATION_TOLERANCE,
}

const TRIP_LIST_DRAG_DISABLED_SELECTOR = [
  '[data-trip-list-drag-disabled="true"]',
  ...INTERACTIVE_DRAG_IGNORE_SELECTORS,
].join(',')

export const dropAnimationSideEffects = defaultDropAnimationSideEffects({
  className: {
    dragOverlay: 'dropped',
  },
  styles: {
    active: {
      visibility: 'hidden',
    },
  },
})

export const autoScrollConfig = {
  threshold: {
    x: 0,
    y: 0.2,
  },
}

interface RowDropTarget {
  type: 'row'
  rowId: string
  placement: DropTargetPlacement
}

interface GroupDropTarget {
  type: 'group'
  groupId: string
  placement: DropTargetPlacement
}

export type DropTarget = RowDropTarget | GroupDropTarget

const tripListSensors = createDragEventSensors(TRIP_LIST_DRAG_DISABLED_SELECTOR)
export const TripListPointerSensor = tripListSensors.PointerSensor
export const TripListTouchSensor = tripListSensors.TouchSensor

export function getDayIdFromGroupId(groupId: string): number | undefined {
  const match = /^(?:empty-)?day:(\d+)$/.exec(groupId)
  if (!match) return undefined

  const dayId = Number(match[1])
  return Number.isFinite(dayId) ? dayId : undefined
}

// Only the first renderable group per stop carries a stopHeader, so fall back to
// the group's own rows to resolve the stop for later day groups in the same stop.
function getTripListGroupStop(group: TripListRenderableGroup): TripStop | undefined {
  return group.group.stopHeader?.stop ?? group.rows[0]?.row.stop
}

// day:/empty-day: groups encode their day in the id; unplanned:/empty-stop: groups
// have no day. Existing rows already carry the resolved day for populated groups.
function getTripListGroupDay(group: TripListRenderableGroup, stop: TripStop | undefined) {
  const dayId = getDayIdFromGroupId(group.id)
  if (dayId !== undefined) return stop?.days.find(day => day.id === dayId)
  return group.rows[0]?.row.day
}

export function getDropPlacementForGroupTarget(placement: DropTargetPlacement): TripListMovePlacement {
  return placement === 'before' ? { placementType: 'start' } : { placementType: 'end' }
}

export function getGroupMoveDayOption(
  group: TripListRenderableGroup,
  placement: DropTargetPlacement,
): TripListMoveDayOption | undefined {
  const stop = getTripListGroupStop(group)
  const day = getTripListGroupDay(group, stop)
  const id = day ? `day:${day.id}` : 'unplanned'

  return {
    id,
    title: group.group.title,
    subtitle: group.group.stopHeader?.title ?? group.group.subtitle ?? group.group.title,
    selected: false,
    target: day ? { targetType: 'day', dayId: day.id } : { targetType: 'unplanned' },
    placement: getDropPlacementForGroupTarget(placement),
  }
}

export function isTripListDropTargetAvailable(
  target: DropTarget,
  _activeRow: TripListRow,
  rowById: ReadonlyMap<string, TripListRow>,
  groupById: ReadonlyMap<string, TripListRenderableGroup>,
): boolean {
  return target.type === 'row' ? rowById.has(target.rowId) : groupById.has(target.groupId)
}

export function getDropPlacementForRowTarget(
  activeRowId: string,
  target: RowDropTarget,
  overRow: TripListRow,
): TripListMovePlacement | undefined {
  if (activeRowId === target.rowId) return undefined

  // Anchor relative to the target row's trip item id (transport rows expose it via
  // `option.itemId`, matching getTripListRowTripItemId) so the persisted placement
  // matches the before/after indicator shown to the user for every row type.
  return {
    placementType: target.placement,
    itemId: getTripListRowTripItemId(overRow),
  }
}

function getTripListDropTargetFromElement(
  element: Element,
  coordinates: PointerCoordinates,
  ignoredRowId: string | undefined,
): DropTarget | undefined {
  const rowElement = element.closest('[data-trip-list-row-id]')
  const rowId = rowElement?.getAttribute('data-trip-list-row-id')
  if (rowElement && rowId && rowId !== ignoredRowId) {
    const rowRect = rowElement.getBoundingClientRect()
    return {
      type: 'row',
      rowId,
      placement: coordinates.y < rowRect.top + rowRect.height / 2 ? 'before' : 'after',
    }
  }

  const groupElement = element.closest('[data-trip-list-group-target-id]')
  const groupId = groupElement?.getAttribute('data-trip-list-group-target-id')
  const placement = groupElement?.getAttribute('data-trip-list-group-target-placement')
  if (groupId && (placement === 'before' || placement === 'after')) {
    return {
      type: 'group',
      groupId,
      placement,
    }
  }

  return undefined
}

export interface TripListRenderableRow {
  row: TripListRow
  showGroupMetadata: boolean
  showLocationMetadata: boolean
  firstRow: boolean
  lastRow: boolean
  selected: boolean
  isBookingPending: boolean
  isNoteUpdatePending: boolean
}

export interface TripListRenderableGroup {
  id: string
  group: TripListGroup
  rows: Array<TripListRenderableRow>
}

export function shouldShowTripListRowMetadata(group: TripListGroup, rowIndex: number): boolean {
  return group.rowMetadataDisplay === 'all' || rowIndex === 0
}

function shouldShowTripListRowLocationMetadata(group: TripListGroup): boolean {
  return group.rowMetadataDisplay === 'all'
}

export function buildTripListRenderableGroups(
  groups: Array<TripListGroup>,
  selectedRowIds: Set<string>,
  bookingRowId: string | undefined,
  noteUpdateRowId: string | undefined,
): Array<TripListRenderableGroup> {
  const firstGroupId = groups[0]?.id
  const lastGroupId = groups[groups.length - 1]?.id

  return groups.map(group => ({
    id: group.id,
    group,
    rows: group.rows.map((row, rowIndex) => ({
      row,
      showGroupMetadata: shouldShowTripListRowMetadata(group, rowIndex),
      showLocationMetadata: shouldShowTripListRowLocationMetadata(group),
      firstRow: firstGroupId === group.id && rowIndex === 0 && !group.stopHeader,
      lastRow: lastGroupId === group.id && rowIndex === group.rows.length - 1,
      selected: selectedRowIds.has(row.id),
      isBookingPending: bookingRowId === row.id,
      isNoteUpdatePending: noteUpdateRowId === row.id,
    })),
  }))
}

export function buildOrderedTripListRenderableGroups(
  renderableGroups: Array<TripListRenderableGroup>,
  rowOrderIndexById: Map<string, number>,
): Array<TripListRenderableGroup> {
  const firstGroupId = renderableGroups[0]?.id
  const lastGroupId = renderableGroups[renderableGroups.length - 1]?.id

  return renderableGroups.map(group => ({
    ...group,
    rows: [...group.rows]
      .sort((firstRow, secondRow) => {
        return (rowOrderIndexById.get(firstRow.row.id) ?? 0) - (rowOrderIndexById.get(secondRow.row.id) ?? 0)
      })
      .map((row, rowIndex, rows) => ({
        ...row,
        showGroupMetadata: shouldShowTripListRowMetadata(group.group, rowIndex),
        showLocationMetadata: shouldShowTripListRowLocationMetadata(group.group),
        firstRow: firstGroupId === group.id && rowIndex === 0 && !group.group.stopHeader,
        lastRow: lastGroupId === group.id && rowIndex === rows.length - 1,
      })),
  }))
}

export function buildOptimisticTripListRenderableGroups(
  renderableGroups: Array<TripListRenderableGroup>,
  rowGroupIdOverrideById: Map<string, string>,
): Array<TripListRenderableGroup> {
  if (rowGroupIdOverrideById.size === 0) return renderableGroups

  const rowById = new Map(renderableGroups.flatMap(group => group.rows.map(row => [row.row.id, row] as const)))

  return renderableGroups.map(group => {
    const targetStop = getTripListGroupStop(group)
    const day = getTripListGroupDay(group, targetStop)
    const movedRows = Array.from(rowGroupIdOverrideById.entries())
      .filter(([, groupId]) => groupId === group.id)
      .map(([rowId]) => rowById.get(rowId))
      .filter(row => row !== undefined)
      .map(row => ({
        ...row,
        row: {
          ...row.row,
          day,
          dayLabel: group.group.title,
          stop: targetStop ?? row.row.stop,
        },
      }))
    const rows = [
      ...group.rows.filter(row => rowGroupIdOverrideById.get(row.row.id) === undefined),
      ...movedRows,
    ]

    return {
      ...group,
      group: {
        ...group.group,
        showEmptyPlaceholder: rows.length === 0 ? group.group.showEmptyPlaceholder : false,
      },
      rows,
    }
  }).filter(group => group.rows.length > 0 || group.group.showEmptyPlaceholder)
}

export { getDropPointFromDragEvent, getPointerCoordinates, moveItemToDropTarget, moveItemToGroupTarget }
export type { DropTargetPlacement, PointerCoordinates }

export function getTripListDropTargetFromPoint(
  coordinates: PointerCoordinates,
  ignoredRowId: string | undefined,
): DropTarget | undefined {
  for (const element of document.elementsFromPoint(coordinates.x, coordinates.y)) {
    const target = getTripListDropTargetFromElement(element, coordinates, ignoredRowId)
    if (target) return target
  }

  return undefined
}

export function isSameDropTarget(first: DropTarget | undefined, second: DropTarget | undefined): boolean {
  if (!first || !second) return first === second
  if (first.type !== second.type || first.placement !== second.placement) return false
  if (first.type === 'row' && second.type === 'row') return first.rowId === second.rowId
  if (first.type === 'group' && second.type === 'group') return first.groupId === second.groupId
  return false
}
